/**
 * NeoPayProofPage — iter197. The "Verified on NeoPay" full-page proof view.
 *
 * Anyone clicking the ledger ✓ badge anywhere in the app lands here.
 * Renders a print-ready Authenticity Certificate with:
 *   • Hero authenticity banner (verified / pending / tampered / not-on-chain)
 *   • Transfer fact-sheet (amount, parties, group, dates)
 *   • Cryptographic proof block (tx_id, block #, memo, merkle root, block hash, signatures)
 *   • On-chain memo vs expected memo comparison (tamper indicator)
 *   • Validator signatures table
 *   • One-tap re-verify, copy-to-clipboard, share, print, view on chain
 *
 * URL: /proof/:transferId
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft, ShieldCheck, ShieldAlert, ShieldOff, Clock, Copy, CheckCircle2,
  Loader2, Printer, RefreshCw, AlertTriangle, FileText, Hash,
  Link2, Calendar, ArrowRight, Users, Lock, Sparkles, Share2,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import { Button } from '../components/ui/button';

const AUTH_CONFIG = {
  verified: {
    label: 'Authentic',
    sub: 'This transaction is sealed in the NeoPay immutable ledger and its proof has been independently verified.',
    Icon: ShieldCheck,
    accent: 'emerald',
    chip: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/40',
    glow: 'shadow-[0_0_60px_rgba(16,185,129,0.18)]',
  },
  settled_offline_verify: {
    label: 'Sealed on chain',
    sub: 'This transaction is permanently recorded on the NeoPay ledger. The external verifier endpoint was not reachable for an independent challenge.',
    Icon: ShieldCheck,
    accent: 'emerald',
    chip: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/40',
    glow: 'shadow-[0_0_60px_rgba(16,185,129,0.14)]',
  },
  pending: {
    label: 'Settling',
    sub: 'The transaction has been broadcast to the chain. We are waiting for the block confirmation.',
    Icon: Clock,
    accent: 'amber',
    chip: 'bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-500/40',
    glow: 'shadow-[0_0_60px_rgba(245,158,11,0.16)]',
  },
  tampered: {
    label: 'Tamper detected',
    sub: 'The on-chain memo does NOT match what was submitted. Contact a super-admin immediately.',
    Icon: ShieldAlert,
    accent: 'red',
    chip: 'bg-red-500/15 text-red-700 dark:text-red-300 border-red-500/40',
    glow: 'shadow-[0_0_60px_rgba(239,68,68,0.22)]',
  },
  not_on_chain: {
    label: 'Not on chain',
    sub: 'This transaction has not been mirrored to the NeoPay ledger yet. A super-admin can replay the mirror from the assurance queue.',
    Icon: ShieldOff,
    accent: 'slate',
    chip: 'bg-slate-500/15 text-slate-700 dark:text-slate-300 border-slate-500/40',
    glow: '',
  },
  unknown: {
    label: 'Unable to verify',
    sub: 'We could not retrieve the proof bundle right now. Try again in a moment.',
    Icon: ShieldAlert,
    accent: 'slate',
    chip: 'bg-slate-500/15 text-slate-700 dark:text-slate-300 border-slate-500/40',
    glow: '',
  },
};

function pickAuth(authenticity) {
  return AUTH_CONFIG[authenticity] || AUTH_CONFIG.unknown;
}

function formatDateTime(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      year: 'numeric', month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit', timeZoneName: 'short',
    });
  } catch {
    return iso;
  }
}

function CurrencyAmount({ amount, currency }) {
  const sym = ({
    INR: '₹', USD: '$', EUR: '€', GBP: '£', SGD: 'S$', AED: 'د.إ',
    AUD: 'A$', CAD: 'C$', JPY: '¥',
  })[currency] || '₹';
  const value = Math.round(Number(amount || 0)).toLocaleString();
  return <span className="tabular-nums">{sym}{value}</span>;
}

function CopyButton({ value, label = 'Copy', testId }) {
  const [copied, setCopied] = useState(false);
  const onCopy = useCallback(async (e) => {
    e?.stopPropagation?.();
    if (!value) return;
    try {
      await navigator.clipboard.writeText(String(value));
      setCopied(true);
      toast.success(`${label} copied`);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error('Could not copy');
    }
  }, [value, label]);
  if (!value) return null;
  return (
    <button
      type="button"
      onClick={onCopy}
      aria-label={`Copy ${label}`}
      data-testid={testId}
      className="ml-2 p-1 rounded-md hover:bg-muted transition-colors text-muted-foreground hover:text-foreground inline-flex items-center"
    >
      {copied ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" /> : <Copy className="w-3.5 h-3.5" />}
    </button>
  );
}

function FactRow({ label, value, mono = false, truncate = false, testId, link, copyable, Icon }) {
  const isEmpty = value === null || value === undefined || value === '';
  return (
    <div className="flex items-start gap-3 py-2 border-b border-border/60 last:border-b-0" data-testid={testId}>
      <div className="flex items-center gap-1.5 min-w-[120px] text-xs uppercase tracking-[0.14em] font-bold text-muted-foreground pt-0.5">
        {Icon && <Icon className="w-3 h-3" />}
        <span>{label}</span>
      </div>
      <div className="flex-1 min-w-0 flex items-start">
        {isEmpty ? (
          <span className="text-sm text-muted-foreground/70">—</span>
        ) : link ? (
          <a
            href={link}
            target="_blank"
            rel="noopener noreferrer"
            className={`text-sm text-primary underline-offset-2 hover:underline ${mono ? 'font-mono' : ''} ${truncate ? 'truncate' : 'break-all'}`}
          >
            {String(value)}
          </a>
        ) : (
          <span className={`text-sm ${mono ? 'font-mono' : ''} ${truncate ? 'truncate' : 'break-all'}`}>
            {String(value)}
          </span>
        )}
        {copyable && !isEmpty && <CopyButton value={value} label={label} testId={testId ? `${testId}-copy` : undefined} />}
      </div>
    </div>
  );
}

export default function NeoPayProofPage() {
  const { transferId, source, id: sourceId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reverifying, setReverifying] = useState(false);

  // Three URL shapes are supported (back-compat with iter197):
  //   /proof/:transferId           → coin transfer (default)
  //   /proof/expense/:id           → expense row
  //   /proof/fund-txn/:id          → fund_transactions row
  const resolved = useMemo(() => {
    if (source === 'expense' && sourceId) {
      return {
        source: 'expense',
        id: sourceId,
        certPath: `/neopay-assurance/expense/${sourceId}/certificate`,
        reverifyPath: `/neopay/expenses/${sourceId}/proof`,
      };
    }
    if ((source === 'fund-txn' || source === 'fund_txn') && sourceId) {
      return {
        source: 'fund_txn',
        id: sourceId,
        certPath: `/neopay-assurance/fund-txn/${sourceId}/certificate`,
        reverifyPath: `/neopay/fund-txns/${sourceId}/proof`,
      };
    }
    return {
      source: 'transfer',
      id: transferId,
      certPath: `/neopay-assurance/transfer/${transferId}/certificate`,
      reverifyPath: `/neopay/transfers/${transferId}/verify`,
    };
  }, [transferId, source, sourceId]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await api.get(resolved.certPath);
      setData(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to load proof');
    } finally {
      setLoading(false);
    }
  }, [resolved.certPath]);

  useEffect(() => { load(); }, [load]);

  const onReverify = useCallback(async () => {
    setReverifying(true);
    try {
      if (resolved.source === 'transfer') {
        await api.post(resolved.reverifyPath);
      } else {
        // For expense / fund_txn we re-fetch proof which forces the
        // backend to ask the chain again.
        await api.get(resolved.reverifyPath);
      }
      await load();
      toast.success('Re-verified against the chain');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Re-verify failed');
    } finally {
      setReverifying(false);
    }
  }, [resolved, load]);

  const onShare = useCallback(async () => {
    const url = window.location.href;
    const text = data?.transfer
      ? `NeoPay proof certificate — ${data.transfer.from_user_name} → ${data.transfer.to_user_name}`
      : 'NeoPay proof certificate';
    try {
      if (navigator.share) {
        await navigator.share({ title: 'Proof of authenticity', text, url });
      } else {
        await navigator.clipboard.writeText(url);
        toast.success('Proof link copied');
      }
    } catch {
      /* user cancelled — ignore */
    }
  }, [data]);

  const auth = useMemo(() => pickAuth(data?.authenticity), [data]);
  const AuthIcon = auth.Icon;
  const proof = data?.chain_proof || {};

  if (loading && !data) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center text-center p-6">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground mb-3" />
        <p className="text-sm text-muted-foreground">Fetching proof certificate…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center text-center p-6">
        <ShieldAlert className="w-8 h-8 text-amber-500 mb-3" />
        <p className="text-sm font-semibold mb-2" data-testid="proof-error">{error}</p>
        <Button onClick={() => navigate(-1)} variant="outline" data-testid="proof-error-back">Go back</Button>
      </div>
    );
  }

  if (!data) return null;

  const { transfer, group, neopay, verify_result, expected_memo, chain_memo, memo_matches } = data;
  const validators = proof?.validator_signatures || proof?.signatures || [];

  return (
    <div className="min-h-screen pb-32 bg-gradient-to-b from-background via-background to-muted/30" data-testid="proof-page">
      {/* Header */}
      <header className="sticky top-0 z-20 bg-background/85 backdrop-blur-lg border-b border-border">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center gap-3">
          <button
            type="button"
            onClick={() => navigate(-1)}
            aria-label="Go back"
            data-testid="proof-back-btn"
            className="p-1.5 rounded-full hover:bg-muted transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex-1 min-w-0">
            <h1 className="font-display text-base font-bold leading-tight">Proof of authenticity</h1>
            <p className="text-xs text-muted-foreground truncate">
              NeoPay immutable ledger · {resolved.source === 'expense' ? 'expense' : resolved.source === 'fund_txn' ? 'fund txn' : 'transfer'} {(resolved.id || '').slice(0, 8)}…
            </p>
          </div>
          <button
            type="button"
            onClick={() => window.print()}
            aria-label="Print proof"
            data-testid="proof-print-btn"
            className="p-2 rounded-full hover:bg-muted transition-colors hidden sm:inline-flex"
          >
            <Printer className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={onShare}
            aria-label="Share proof"
            data-testid="proof-share-btn"
            className="p-2 rounded-full hover:bg-muted transition-colors"
          >
            <Share2 className="w-4 h-4" />
          </button>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-6 space-y-6">
        {/* HERO — Authenticity banner */}
        <section
          className={`relative rounded-3xl border-2 bg-card p-6 ${auth.glow} overflow-hidden`}
          data-testid={`proof-hero-${data.authenticity}`}
        >
          <div className="absolute -top-12 -right-12 w-48 h-48 rounded-full bg-gradient-to-br from-primary/10 to-transparent" />
          <div className="relative flex items-start gap-4">
            <div className={`w-14 h-14 rounded-2xl border-2 flex items-center justify-center ${auth.chip}`}>
              <AuthIcon className="w-7 h-7" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center flex-wrap gap-2 mb-1">
                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs uppercase tracking-[0.2em] font-bold border ${auth.chip}`}>
                  <Sparkles className="w-3 h-3" />
                  {auth.label}
                </span>
                {neopay.block_number && (
                  <span className="text-xs font-mono px-2 py-0.5 rounded-full border border-border bg-muted/40 text-muted-foreground">
                    Block #{neopay.block_number}
                  </span>
                )}
              </div>
              <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight leading-tight mb-2">
                {transfer.from_user_name || 'Unknown'} → {transfer.to_user_name || 'Unknown'}
              </h2>
              <p className="text-sm text-muted-foreground leading-relaxed">
                {auth.sub}
              </p>
            </div>
          </div>

          {/* Big-amount strip */}
          <div className="mt-5 pt-5 border-t border-border/60 grid grid-cols-3 gap-3 text-center">
            <div>
              <p className="text-xs uppercase tracking-[0.16em] font-bold text-muted-foreground">Amount</p>
              <p className="font-display text-2xl font-bold mt-0.5" data-testid="proof-hero-amount">
                <CurrencyAmount amount={transfer.amount} currency={group.currency} />
              </p>
            </div>
            <div>
              <p className="text-xs uppercase tracking-[0.16em] font-bold text-muted-foreground">Settled</p>
              <p className="text-xs font-semibold mt-1 leading-tight" data-testid="proof-hero-settled">
                {formatDateTime(neopay.settled_at || transfer.approved_at)}
              </p>
            </div>
            <div>
              <p className="text-xs uppercase tracking-[0.16em] font-bold text-muted-foreground">Validators</p>
              <p className="font-display text-2xl font-bold mt-0.5 tabular-nums" data-testid="proof-hero-validators">
                {validators.length || 0}
              </p>
            </div>
          </div>

          {/* CTA row */}
          <div className="mt-5 flex flex-wrap gap-2">
            <Button
              size="sm"
              onClick={onReverify}
              disabled={reverifying || !neopay.tx_id}
              data-testid="proof-reverify-btn"
              className="gap-1.5"
            >
              {reverifying ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
              {reverifying ? 'Re-verifying…' : 'Re-verify on chain'}
            </Button>
          </div>
        </section>

        {/* TRANSFER FACT-SHEET */}
        <section className="rounded-2xl border-2 border-border bg-card p-5" data-testid="proof-transfer-card">
          <header className="flex items-center gap-2 mb-3">
            <FileText className="w-4 h-4 text-primary" />
            <h3 className="font-display text-sm font-bold uppercase tracking-[0.18em]">Transaction</h3>
          </header>
          <div className="space-y-0">
            <FactRow Icon={Hash} label="Transfer ID" value={transfer.id} mono copyable testId="proof-row-transfer-id" />
            <FactRow Icon={Users} label="From" value={transfer.from_user_name} testId="proof-row-from" />
            <FactRow Icon={ArrowRight} label="To" value={transfer.to_user_name} testId="proof-row-to" />
            <FactRow label="Group" value={`${group.name} (${group.group_type})`} testId="proof-row-group" />
            <FactRow label="Note" value={transfer.note} testId="proof-row-note" />
            <FactRow Icon={Calendar} label="Created" value={formatDateTime(transfer.created_at)} testId="proof-row-created" />
            <FactRow Icon={Calendar} label="Approved" value={formatDateTime(transfer.approved_at)} testId="proof-row-approved" />
          </div>
        </section>

        {/* CRYPTOGRAPHIC PROOF */}
        <section className="rounded-2xl border-2 border-border bg-card p-5" data-testid="proof-crypto-card">
          <header className="flex items-center gap-2 mb-3">
            <Lock className="w-4 h-4 text-primary" />
            <h3 className="font-display text-sm font-bold uppercase tracking-[0.18em]">Cryptographic proof</h3>
          </header>
          <div className="space-y-0">
            <FactRow
              Icon={Hash}
              label="Tx ID"
              value={neopay.tx_id}
              mono
              copyable
              testId="proof-row-tx-id"
            />
            <FactRow
              label="Block #"
              value={neopay.block_number}
              mono
              testId="proof-row-block-number"
            />
            <FactRow
              Icon={Link2}
              label="Proof URL"
              value={neopay.proof_url}
              mono
              truncate
              copyable
              testId="proof-row-proof-url"
            />
            <FactRow
              label="Merkle root"
              value={proof.merkle_root || proof.merkleRoot}
              mono
              truncate
              copyable
              testId="proof-row-merkle"
            />
            <FactRow
              label="Block hash"
              value={proof.block_hash || proof.blockHash}
              mono
              truncate
              copyable
              testId="proof-row-block-hash"
            />
            <FactRow
              label="Previous hash"
              value={proof.previous_hash || proof.previousHash || proof.prev_hash}
              mono
              truncate
              copyable
              testId="proof-row-prev-hash"
            />
            <FactRow
              label="Signature"
              value={proof.signature}
              mono
              truncate
              copyable
              testId="proof-row-signature"
            />
            <FactRow
              label="Status"
              value={neopay.status}
              testId="proof-row-neopay-status"
            />
            <FactRow
              label="Submitted"
              value={formatDateTime(neopay.submitted_at)}
              testId="proof-row-submitted"
            />
          </div>
        </section>

        {/* MEMO COMPARISON */}
        <section className="rounded-2xl border-2 border-border bg-card p-5" data-testid="proof-memo-card">
          <header className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Hash className="w-4 h-4 text-primary" />
              <h3 className="font-display text-sm font-bold uppercase tracking-[0.18em]">Memo integrity</h3>
            </div>
            {chain_memo && (
              <span
                className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold uppercase tracking-wider border ${
                  memo_matches
                    ? 'border-emerald-500/40 text-emerald-700 dark:text-emerald-300 bg-emerald-500/10'
                    : 'border-red-500/40 text-red-700 dark:text-red-300 bg-red-500/10'
                }`}
                data-testid={`proof-memo-match-${memo_matches ? 'ok' : 'fail'}`}
              >
                {memo_matches ? (
                  <><CheckCircle2 className="w-3 h-3" /> Matches</>
                ) : (
                  <><AlertTriangle className="w-3 h-3" /> Mismatch</>
                )}
              </span>
            )}
          </header>
          <div className="space-y-3 text-xs">
            <div>
              <p className="text-xs uppercase tracking-[0.14em] font-bold text-muted-foreground mb-1">Expected (signed by app)</p>
              <pre className="bg-muted/50 rounded-lg p-3 font-mono break-all whitespace-pre-wrap" data-testid="proof-expected-memo">
                {expected_memo || '—'}
              </pre>
            </div>
            <div>
              <p className="text-xs uppercase tracking-[0.14em] font-bold text-muted-foreground mb-1">On chain (NeoPay)</p>
              <pre className="bg-muted/50 rounded-lg p-3 font-mono break-all whitespace-pre-wrap" data-testid="proof-chain-memo">
                {chain_memo || '— not on chain —'}
              </pre>
            </div>
          </div>
        </section>

        {/* VALIDATORS */}
        {validators.length > 0 && (
          <section className="rounded-2xl border-2 border-border bg-card p-5" data-testid="proof-validators-card">
            <header className="flex items-center gap-2 mb-3">
              <ShieldCheck className="w-4 h-4 text-primary" />
              <h3 className="font-display text-sm font-bold uppercase tracking-[0.18em]">
                Validator signatures · {validators.length}
              </h3>
            </header>
            <div className="space-y-2 text-xs">
              {validators.slice(0, 12).map((v, i) => {
                const name = typeof v === 'string'
                  ? `validator-${i + 1}`
                  : v.validator || v.node || `validator-${i + 1}`;
                const sig = typeof v === 'string' ? v : (v.signature || v.sig);
                return (
                  <div
                    key={i}
                    className="flex items-start gap-3 py-2 border-b border-border/60 last:border-b-0"
                    data-testid={`proof-validator-${i}`}
                  >
                    <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 text-xs font-bold">
                      {i + 1}
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="font-mono text-xs font-bold">{name}</p>
                      {sig && (
                        <p className="font-mono text-xs text-muted-foreground truncate" title={sig}>
                          {sig}
                        </p>
                      )}
                    </div>
                    <CopyButton value={sig} label="signature" testId={`proof-validator-${i}-copy`} />
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {/* VERIFICATION RESULT */}
        {verify_result && (
          <section
            className={`rounded-2xl border-2 p-4 flex items-center gap-3 ${
              verify_result.valid
                ? 'border-emerald-500/40 bg-emerald-500/10'
                : 'border-red-500/40 bg-red-500/10'
            }`}
            data-testid="proof-verify-banner"
          >
            {verify_result.valid ? (
              <CheckCircle2 className="w-5 h-5 text-emerald-600 dark:text-emerald-400 shrink-0" />
            ) : (
              <ShieldAlert className="w-5 h-5 text-red-600 dark:text-red-400 shrink-0" />
            )}
            <div className="flex-1 text-xs">
              <p className="font-semibold">
                {verify_result.valid
                  ? 'Independent verification passed'
                  : 'Independent verification failed'}
              </p>
              {verify_result.error && (
                <p className="text-xs text-muted-foreground mt-0.5">{verify_result.error}</p>
              )}
            </div>
          </section>
        )}

        {/* FOOTER — verify timestamp + brand */}
        <footer className="text-center text-xs text-muted-foreground space-y-1 pt-4">
          <p>
            Certificate generated {formatDateTime(data.generated_at)}.
          </p>
          <p>
            Authenticity sealed by NeoPay immutable ledger · {neopay.base_url || 'chain offline'}
          </p>
        </footer>
      </main>
    </div>
  );
}
